import React from 'react';
import ReactSVG from 'react-svg';
import { colors } from '~/layout';
import { useTranslation } from '~/dependencies';
import { TimeToRespondIcon } from '~/assets/svgIcons';
import { Chip } from './styles';

type TTRBadgeProps = {
  expireIn: number | null;
};

const TTR_THRESHOLDS = {
  CRITICAL_MINUTES: 60,
  WARNING_MINUTES: 720, // 12 hours
} as const;

const TTR_COLORS = {
  CRITICAL: colors.red09,
  WARNING: colors.yellow04,
  NORMAL: colors.lightBlue13,
  EXPIRED: colors.darkGray12,
} as const;

function formatTimeUnits(minutes: number): string {
  if (minutes < TTR_THRESHOLDS.CRITICAL_MINUTES) {
    return `${minutes}min`;
  }

  const hours = Math.floor(minutes / 60);
  return `${hours}h`;
}

function getColorAndStatus(
  expireIn: number
): { color: string; label: string; isExpired?: boolean } {
  if (expireIn <= 0) {
    return { color: TTR_COLORS.EXPIRED, label: 'Expired', isExpired: true };
  }

  if (expireIn < TTR_THRESHOLDS.CRITICAL_MINUTES) {
    return {
      color: TTR_COLORS.CRITICAL,
      label: formatTimeUnits(expireIn),
      isExpired: false,
    };
  }

  if (expireIn <= TTR_THRESHOLDS.WARNING_MINUTES) {
    return {
      color: TTR_COLORS.WARNING,
      label: formatTimeUnits(expireIn),
      isExpired: false,
    };
  }

  return {
    color: TTR_COLORS.NORMAL,
    label: formatTimeUnits(expireIn),
    isExpired: false,
  };
}

const TTRBadge = React.memo(({ expireIn }: TTRBadgeProps) => {
  if (expireIn === null || expireIn === undefined) return null;

  const { t } = useTranslation();

  const { color, label, isExpired } = getColorAndStatus(expireIn);

  const title = `Time to respond: ${label}`;
  const labelI18n = isExpired
    ? t('screens.conversation.ttrBadge.expired')
    : label;

  return (
    <Chip
      color={color}
      title={title}
      label={labelI18n}
      avatar={<ReactSVG src={TimeToRespondIcon} />}
    />
  );
});

export default TTRBadge;
